// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Feed & Inbox
// See broadcasts + messages targeted to your org, post replies
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Textarea,
  Select, Modal, ActionIcon, Stack, SegmentedControl, Collapse,
} from '../../../components/gab/GabComponents';
import { useDisclosure } from '../../../components/gab/GabHooks';
import { notifications } from '../../../lib/notifications';
import {
  IconPlus, IconRefresh, IconTrash, IconSend,
  IconInbox, IconSpeakerphone, IconCornerDownRight,
  IconChevronDown, IconChevronRight,
} from '@tabler/icons-react';
import { useMainStreetOpsStore } from '../../../store/mainstreetOpsStore';
import type { MSPost } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { fmtDateTime } from '../../../lib/dateFormat';

const PORTAL_POST_TYPES = [
  { value: 'question', label: 'Question' },
  { value: 'update', label: 'Status Update' },
];

const TYPE_COLORS: Record<string, string> = {
  announcement: 'var(--color-primary-700)', message: 'var(--data-viz-650)', question: 'var(--data-viz-650)',
  update: '#03AC9E', alert: 'var(--color-error)',
};

const PRIORITY_COLORS: Record<string, string> = {
  normal: '#939598', urgent: '#F86A0B', critical: 'var(--color-error)',
};

const BORDER_COLORS: Record<string, string> = {
  urgent: '#F86A0B',
  critical: 'var(--color-error)',
};

export default function PortalFeed() {
  const { posts, loading, loadPosts, createPost, deletePost } = useMainStreetOpsStore();
  const { activeIncident, user } = useAuthStore();
  const myOrg = useMyOrg();
  const myOrgId = myOrg ? String(myOrg.id) : '';

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('update');
  const [saving, setSaving] = useState(false);

  const [viewMode, setViewMode] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState<MSPost | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  // Reply
  const [replyTo, setReplyTo] = useState<MSPost | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyingSaving, setReplyingSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Separate top-level from replies
  const topLevelPosts = useMemo(() => posts.filter(p => !p.parentPostId), [posts]);
  const repliesByParent = useMemo(() => {
    const map: Record<string, MSPost[]> = {};
    posts.filter(p => p.parentPostId).forEach(p => {
      if (!map[p.parentPostId]) map[p.parentPostId] = [];
      map[p.parentPostId].push(p);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')));
    return map;
  }, [posts]);

  // Show: broadcasts + messages targeted to my org
  const filtered = useMemo(() => {
    let list = topLevelPosts;
    if (activeIncident) {
      list = list.filter(p => String(p.incidentId) === String(activeIncident.id));
    }
    // Filter to posts visible to this business
    list = list.filter(p => {
      // Broadcasts visible to all
      if (!p.targetType || p.targetType === 'broadcast') return true;
      // Targeted to my org
      if (p.targetOrgIds && myOrgId) {
        const ids = p.targetOrgIds.split(',').map(s => s.trim());
        if (ids.includes(myOrgId)) return true;
      }
      // My own posts
      if (p.authorName === (user?.fullName ?? user?.email)) return true;
      return false;
    });
    if (viewMode === 'inbox') {
      list = list.filter(p => p.targetType && p.targetType !== 'broadcast');
    } else if (viewMode === 'broadcasts') {
      list = list.filter(p => !p.targetType || p.targetType === 'broadcast');
    }
    return [...list].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  }, [topLevelPosts, activeIncident, viewMode, myOrgId, user]);

  const inboxCount = useMemo(() => {
    if (!myOrgId) return 0;
    return topLevelPosts.filter(p => {
      if (!p.targetType || p.targetType === 'broadcast') return false;
      if (p.targetOrgIds) {
        return p.targetOrgIds.split(',').map(s => s.trim()).includes(myOrgId);
      }
      return false;
    }).length;
  }, [topLevelPosts, myOrgId]);

  async function handleCreate() {
    if (!content.trim()) {
      notifications.show({ color: 'var(--color-error)', title: 'Required', message: 'Content is required.' });
      return;
    }
    setSaving(true);
    await createPost({
      incidentId: activeIncident ? String(activeIncident.id) : '',
      content: content.trim(),
      postType,
      priority: 'normal',
      authorName: user?.fullName ?? user?.email ?? 'Unknown',
      createdAt: new Date().toISOString(),
    });
    setSaving(false);
    notifications.show({ color: 'var(--color-success)', title: 'Posted', message: 'Post published.' });
    setContent(''); setPostType('update');
    closeCreate();
  }

  async function handleReply() {
    if (!replyTo || !replyContent.trim()) return;
    setReplyingSaving(true);
    await createPost({
      incidentId: activeIncident ? String(activeIncident.id) : '',
      content: replyContent.trim(),
      postType: 'update',
      priority: 'normal',
      authorName: user?.fullName ?? user?.email ?? 'Unknown',
      createdAt: new Date().toISOString(),
      parentPostId: String(replyTo.id),
    });
    setReplyingSaving(false);
    notifications.show({ color: 'var(--color-success)', message: 'Reply sent.' });
    setReplyContent('');
    setReplyTo(null);
    setExpandedIds(prev => new Set(prev).add(replyTo.id));
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deletePost(deleteTarget.id);
    closeDelete();
    setDeleteTarget(null);
  }

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const currentUserName = user?.fullName ?? user?.email ?? '';

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Feed & Inbox</Title>
          {inboxCount > 0 && (
            <Badge size="sm" color="var(--data-viz-650)" variant="filled">{inboxCount} messages</Badge>
          )}
        </Group>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadPosts()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            New Post
          </Button>
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <SegmentedControl size="xs" data={[
          { value: 'all', label: 'All' },
          { value: 'inbox', label: `Inbox${inboxCount > 0 ? ` (${inboxCount})` : ''}` },
          { value: 'broadcasts', label: 'Broadcasts' },
        ]} value={viewMode} onChange={setViewMode} />
      </Group>

      <Text size="xs" c="dimmed" mb="md">{filtered.length} post{filtered.length !== 1 ? 's' : ''}</Text>

      {filtered.length === 0 && (
        <Paper p="xl" withBorder>
          <Text ta="center" c="dimmed">{loading ? 'Loading...' : viewMode === 'inbox' ? 'No messages for your organization.' : 'No posts found.'}</Text>
        </Paper>
      )}

      {filtered.map(post => {
        const borderColor = BORDER_COLORS[post.priority];
        const isOwn = post.authorName === currentUserName;
        const isTargeted = post.targetType && post.targetType !== 'broadcast';
        const replies = repliesByParent[String(post.id)] || [];
        const isExpanded = expandedIds.has(post.id);

        return (
          <Paper key={post.id} p="sm" mb="sm" withBorder
            style={{
              ...(borderColor ? { borderLeft: `4px solid ${borderColor}` } : {}),
              ...(isTargeted ? { background: 'rgba(166,39,255,0.08)' } : {}),
            }}>
            <Group justify="space-between" wrap="nowrap" mb={4}>
              <Group gap="xs">
                {isTargeted ? <IconInbox size={14} color="#7c3aed" /> : <IconSpeakerphone size={14} color="#888" />}
                <Text size="sm" fw={600}>{post.authorName}</Text>
                <Badge size="xs" color={TYPE_COLORS[post.postType] ?? '#939598'}>{post.postType}</Badge>
                {post.priority !== 'normal' && (
                  <Badge size="xs" color={PRIORITY_COLORS[post.priority] ?? '#939598'}>{post.priority}</Badge>
                )}
                {isTargeted && <Badge size="xs" variant="light" color="var(--data-viz-650)">Direct Message</Badge>}
              </Group>
              <Group gap={4}>
                <Text size="xs" c="dimmed">{fmtDateTime(post.createdAt)}</Text>
                <ActionIcon size="xs" variant="subtle" color="#939598"
                  onClick={() => { setReplyTo(post); setReplyContent(''); }}>
                  <IconCornerDownRight size={12} />
                </ActionIcon>
                {isOwn && (
                  <ActionIcon size="xs" variant="subtle" color="var(--color-error)"
                    onClick={() => { setDeleteTarget(post); openDelete(); }}>
                    <IconTrash size={12} />
                  </ActionIcon>
                )}
              </Group>
            </Group>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{post.content}</Text>

            {/* Replies */}
            {replies.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Button variant="subtle" size="compact-xs" color="#939598"
                  leftSection={isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                  onClick={() => toggleExpand(post.id)}>
                  {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </Button>
                <Collapse in={isExpanded}>
                  <Stack gap={4} mt={4} ml={16} style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 16 }}>
                    {replies.map(reply => (
                      <div key={reply.id}>
                        <Group gap="xs" mb={2}>
                          <Text size="xs" fw={600}>{reply.authorName}</Text>
                          <Text size="xs" c="dimmed">{fmtDateTime(reply.createdAt)}</Text>
                        </Group>
                        <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{reply.content}</Text>
                      </div>
                    ))}
                  </Stack>
                </Collapse>
              </div>
            )}

            {/* Inline reply */}
            {replyTo?.id === post.id && (
              <Paper p="xs" mt="sm" withBorder style={{ background: '#F8F8F8' }}>
                <Textarea size="xs" placeholder="Write a reply..." value={replyContent}
                  onChange={e => setReplyContent(e.target.value)}
                  autosize minRows={2} mb="xs" />
                <Group justify="flex-end" gap={4}>
                  <Button size="compact-xs" variant="subtle" onClick={() => setReplyTo(null)}>Cancel</Button>
                  <Button size="compact-xs" loading={replyingSaving} onClick={handleReply}
                    leftSection={<IconSend size={10} />}>Reply</Button>
                </Group>
              </Paper>
            )}
          </Paper>
        );
      })}

      {/* ── Create Modal ────────────────────────────────────── */}
      <Modal opened={createOpened} onClose={closeCreate} title="New Post" size="md">
        <Textarea label="Content" value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Write your post or question..." size="sm" mb="sm" autosize minRows={4} required />
        <Select label="Post Type" data={PORTAL_POST_TYPES} value={postType}
          onChange={v => setPostType(v ?? 'update')} size="sm" mb="md" />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconSend size={16} />}>Post</Button>
        </Group>
      </Modal>

      {/* ── Delete Confirmation ─────────────────────────────── */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Post" size="sm">
        <Text size="sm" mb="md">Delete this post? This cannot be undone.</Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="var(--color-error)" size="xs" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
